import { useEffect, useState } from "react";
import {
  ArrowDown,
  ArrowUpRight,
  Code2,
  Database,
  Layers3,
  Building2,
  Monitor,
  Settings,
  Zap,
} from "lucide-react";
import { Link } from "react-router-dom";
import LandingImage from "../components/LandingImage";
import ProjectContactForm from "../components/ProjectContactForm";
import { CookiePreferencesButton } from "../components/CookieConsent";
import "./AntaresLanding.css";

const WHATSAPP =
  "https://wa.me/5511930628624?text=Ol%C3%A1!%20Gostaria%20de%20conversar%20sobre%20um%20projeto%20com%20a%20Antares.";
const navigation = [
  {
    href: "#solucoes",
    label: "Soluções",
    icon: Code2,
    description: "Tecnologia feita para seu negócio",
  },
  {
    href: "#produtos",
    label: "Produtos",
    icon: Layers3,
    description: "Conheça o ZZBlu e o ZZBlu3D",
  },
  {
    href: "#antares",
    label: "A Antares",
    icon: Building2,
    description: "Como construímos juntos",
  },
];

const services = [
  {
    id: "sites",
    title: "Sites e landing pages",
    description: "Apresente sua marca e facilite o contato de novos clientes.",
    image: "/antares-services/sites-landing-pages.png",
  },
  {
    id: "aplicativos",
    title: "Aplicativos",
    description: "Leve seus serviços para o celular dos seus clientes.",
    image:
      "/antares-services/aplicativos.png",
  },
  {
    id: "sistemas",
    title: "Sistemas sob medida",
    description:
      "Organize processos e informações em uma solução para sua empresa.",
    image: "/antares-services/sistemas-sob-medida.png",
  },
  {
    id: "integracoes",
    title: "Integrações",
    description:
      "Conecte sistemas, pagamentos e dados para reduzir retrabalho.",
    image: "/antares-services/integracoes.png",
  },
  {
    id: "automacao",
    title: "Automação de processos",
    description: "Simplifique tarefas repetitivas e ganhe tempo na operação.",
    image:
      "/antares-services/automacao-processos.png",
  },
  {
    id: "ia",
    title: "IA aplicada ao negócio",
    description:
      "Apoie o atendimento, a consulta de informações e as rotinas da equipe.",
    image: "/antares-services/inteligencia-artificial.png",
  },
];

function Logo() {
  return (
    <a
      className="antares-brand"
      href="#inicio"
      aria-label="Antares Tecnologia GTA — início"
    >
      <LandingImage src="/logoAntares.png" alt="Antares Tecnologia GTA" sizes="320px" priority />
    </a>
  );
}

function SolutionVisual() {
  return (
    <div
      className="solution-visual"
      aria-label="Sistemas, integrações e automação conectados"
      role="img"
    >
      <div className="visual-orbit" />
      <div className="visual-dots dots-top" />
      <div className="visual-dots dots-bottom" />
      <svg
        className="solution-connections"
        viewBox="0 0 480 390"
        fill="none"
        aria-hidden="true"
      >
        <path
          d="M180 112h34c54 0 10 55 71 55M112 180v38q0 51 47 51M377 235v21q0 42-49 42"
          stroke="#9270ff"
        />
        {[
          [180, 112],
          [285, 167],
          [112, 180],
          [159, 269],
          [377, 235],
          [328, 298],
        ].map(([cx, cy], i) => (
          <circle key={i} cx={cx} cy={cy} r="4.5" fill="#5130ff" />
        ))}
      </svg>
      <div className="solution-tile tile-systems">
        <Monitor />
        <strong>Sistemas</strong>
        <span />
        <span />
        <span />
      </div>
      <div className="solution-tile tile-integrations">
        <Settings />
        <strong>Integrações</strong>
        <span />
        <span />
        <span />
      </div>
      <div className="solution-tile tile-automation">
        <Zap />
        <strong>Automação</strong>
        <span />
        <span />
        <span />
      </div>
    </div>
  );
}

export default function AntaresLanding() {
  const [activeSection, setActiveSection] = useState("#solucoes");
  useEffect(() => {
    const updateSection = () => {
      let current = navigation[0].href;
      navigation.forEach(({ href }) => {
        if (
          (document.querySelector(href)?.getBoundingClientRect().top ??
            Infinity) <= 180
        )
          current = href;
      });
      setActiveSection(current);
    };
    window.addEventListener("scroll", updateSection, { passive: true });
    updateSection();
    return () => window.removeEventListener("scroll", updateSection);
  }, []);
  return (
    <div className="antares-site" id="inicio">
      <a href="#conteudo" className="skip-link">
        Pular para o conteúdo
      </a>
      <header className="antares-header">
        <div className="antares-container header-inner">
          <a className="header-brand" href="#inicio" aria-label="Antares Tecnologia GTA — início">
            <img src="/favicon-v2.svg" alt="" width="44" height="44" />
            <span>Antares<span className="header-brand-caption">Tecnologia GTA</span></span>
          </a>
          <nav className="desktop-navigation" aria-label="Navegação principal">
            {navigation.map((item) => (
              <a
                key={item.href}
                href={item.href}
                aria-current={
                  activeSection === item.href ? "location" : undefined
                }
              >
                <item.icon size={17} aria-hidden="true" />
                {item.label}
              </a>
            ))}
          </nav>
          <a
            className="antares-button button-dark header-contact"
            href={WHATSAPP} data-analytics-id="header_contact" data-analytics-event="click_whatsapp" data-analytics-location="header"
            target="_blank"
            rel="noopener noreferrer"
          >
            <span className="header-contact-full">Vamos conversar</span>
            <span className="header-contact-short">Contato</span>
            <span className="contact-arrow">
              <ArrowUpRight size={17} />
            </span>
          </a>
        </div>
      </header>
      <main id="conteudo">
        <section
          className="hero-section"
          id="apresentacao"
          aria-labelledby="hero-title"
        >
          <div className="antares-container hero-grid">
            <div className="hero-copy">
              <p className="eyebrow">
                Software · Automação · Produtos digitais
              </p>
              <h1 id="hero-title">
                Transformamos
                <br />
                desafios em
                <br />
                <span>soluções digitais.</span>
              </h1>
              <p className="hero-description">
                Tecnologia sob medida para simplificar
                <br className="desktop-break" /> operações e tirar ideias do
                papel.
              </p>
              <div className="hero-actions">
                <a
                  className="antares-button button-purple"
                  href={WHATSAPP} data-analytics-id="hero_contact" data-analytics-event="click_whatsapp" data-analytics-location="hero"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Converse sobre seu projeto <ArrowUpRight size={18} />
                </a>
                <a className="explore-link" href="#produtos">
                  Explore nossos produtos <ArrowDown size={18} />
                </a>
              </div>
            </div>
            <SolutionVisual />
          </div>
        </section>
        <div className="antares-container">
          <ul className="services-strip" aria-label="Nossas soluções">
            <li>
              <Code2 />
              <span>Desenvolvimento sob medida</span>
            </li>
            <li>
              <Database />
              <span>Integração de sistemas</span>
            </li>
            <li>
              <Zap />
              <span>Automação de processos</span>
            </li>
          </ul>
        </div>
        <section
          className="antares-container services-section"
          id="solucoes"
          aria-labelledby="services-title"
        >
          <div className="services-heading">
            <p className="eyebrow">Soluções para sua empresa</p>
            <h2 id="services-title">
              O que podemos construir
              <br className="desktop-break" /> para o seu negócio<span>.</span>
            </h2>
            <p>
              Da presença digital à automação da operação, soluções
              <br className="desktop-break" /> alinhadas à sua necessidade.
            </p>
          </div>
          <div className="services-grid">
            {services.map((service) => (
              <article className="service-card" key={service.id} data-analytics-view="service" data-analytics-service={service.id}>
                <div className="service-art">
                  <LandingImage
                    src={service.image}
                    alt=""
                    sizes="(max-width: 599px) calc(100vw - 64px), (max-width: 800px) 45vw, (max-width: 1200px) 30vw, 390px"
                  />
                </div>
                <div className="service-copy">
                  <h3>{service.title}</h3>
                  <p>{service.description}</p>
                  <a
                    href={`https://wa.me/5511930628624?text=${encodeURIComponent(`Olá! Gostaria de conversar sobre ${service.title} com a Antares.`)}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label={`Conversar sobre ${service.title}`}
                    data-analytics-id={`service_${service.id}`} data-analytics-event="click_whatsapp" data-analytics-location="services" data-analytics-service={service.id}
                  >
                    Conversar sobre esta solução{" "}
                    <ArrowUpRight size={18} aria-hidden="true" />
                  </a>
                </div>
              </article>
            ))}
          </div>
          <div className="services-contact">
            <div>
              <h3>Não sabe por onde começar?</h3>
              <p>Conte seu desafio. A gente ajuda a definir o próximo passo.</p>
            </div>
            <a
              className="antares-button button-dark"
              href={WHATSAPP} data-analytics-id="services_contact" data-analytics-event="click_whatsapp" data-analytics-location="services"
              target="_blank"
              rel="noopener noreferrer"
            >
              Fale com a Antares <ArrowUpRight size={20} />
            </a>
          </div>
        </section>
        <section
          className="antares-container products-section"
          id="produtos"
          aria-labelledby="products-title"
        >
          <div className="products-heading">
            <p className="eyebrow">Nossos produtos</p>
            <h2 id="products-title">Ideias que viraram produto.</h2>
            <p>Conheça as soluções desenvolvidas pela Antares.</p>
          </div>
          <article className="product-card zzblu-card">
            <div className="zzblu-art" aria-hidden="true">
              <LandingImage src="/telaHomeAgendamento.png" alt="" sizes="(max-width: 800px) 90vw, 650px" />
            </div>
            <div className="product-copy">
              <span className="product-label">
                Gestão para negócios de beleza
              </span>
              <h3 className="product-logo">
                <LandingImage src="/SessionZZBlu.png" alt="ZZBlu" sizes="420px" />
              </h3>
              <h4>Mais organização. Mais tempo para atender.</h4>
              <p>Para organizar a rotina de negócios de beleza: agenda, clientes e serviços em um só lugar. Aplicativo Flutter integrado a uma API .NET.</p>
              <div className="product-actions">
                <a
                  className="antares-button button-white"
                  href="https://zzblu.com.br" data-analytics-id="product_zzblu" data-analytics-event="click_product" data-analytics-location="products" data-analytics-product="zzblu"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Conhecer o ZZBlu <ArrowUpRight size={19} />
                </a>
                <a
                  className="product-domain"
                  href="https://zzblu.com.br" data-analytics-id="domain_zzblu" data-analytics-event="click_product" data-analytics-location="products" data-analytics-product="zzblu"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  zzblu.com.br
                </a>
              </div>
            </div>
          </article>
          <article className="product-card printing-card">
            <div className="printing-art">
              <LandingImage
                src="/FundoZZBlu3d.png"
                alt="Ilustração 3D de um cubo azul com a letra Z, cercado por ferramentas de gestão"
                sizes="(max-width: 800px) 100vw, 700px"
              />
            </div>
            <div className="product-copy">
              <span className="product-label">
                Precificação de impressão 3D
              </span>
              <h3 className="product-logo">
                <LandingImage src="/SessionZZBlu3d.png" alt="ZZBlu3D" sizes="420px" />
              </h3>
              <h4>
                Clareza para definir o preço
                <br className="desktop-break" /> das suas impressões.
              </h4>
              <p>Para quem precisa definir preços de impressão 3D: uma solução dedicada ao apoio à precificação dos trabalhos.</p>
              <div className="product-actions">
                <a
                  className="antares-button button-blue"
                  href="https://3d.zzblu.com.br" data-analytics-id="product_zzblu3d" data-analytics-event="click_product" data-analytics-location="products" data-analytics-product="zzblu3d"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Conhecer o ZZBlu3D <ArrowUpRight size={19} />
                </a>
                <a
                  className="product-domain"
                  href="https://3d.zzblu.com.br" data-analytics-id="domain_zzblu3d" data-analytics-event="click_product" data-analytics-location="products" data-analytics-product="zzblu3d"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  3d.zzblu.com.br
                </a>
              </div>
            </div>
          </article>
        </section>
        <section
          className="antares-container process-section"
          id="antares"
          aria-labelledby="process-title"
        >
          <div className="process-heading">
            <p className="eyebrow">Como trabalhamos</p>
            <h2 id="process-title">
              Próximos passos,
              <br />
              construídos juntos.
            </h2>
          </div>
          <ol className="process-steps">
            {[
              {
                title: "Entender",
                text: "Conversamos sobre seu desafio e suas prioridades.",
              },
              {
                title: "Construir",
                text: "Desenvolvemos a solução com acompanhamento próximo.",
              },
              {
                title: "Evoluir",
                text: "Ajustamos o produto conforme as necessidades do negócio.",
              },
            ].map((step, index) => (
              <li key={step.title}>
                <span className="step-number">0{index + 1}</span>
                <div>
                  <h3>{step.title}</h3>
                  <p>{step.text}</p>
                </div>
              </li>
            ))}
          </ol>
        </section>
        <section className="antares-container faq-section" id="perguntas" aria-labelledby="faq-title">
          <p className="eyebrow">Antes de começar</p>
          <h2 id="faq-title">Perguntas frequentes</h2>
          {[
            { id: 'projetos', question: 'Que tipos de projeto a Antares desenvolve?', answer: 'Sites, landing pages, aplicativos, sistemas sob medida, integrações, automações e soluções com IA aplicada ao negócio.' },
            { id: 'contato', question: 'Como iniciar uma conversa?', answer: 'Use um dos botões de WhatsApp para contar seu desafio. Nos cartões de serviços, a mensagem já indica a solução de interesse.' },
            { id: 'levantamento', question: 'Como funciona o levantamento de necessidades?', answer: 'O primeiro passo é conversar sobre o desafio e as prioridades do negócio, como descrevemos na seção Como trabalhamos.' },
            { id: 'prazo_orcamento', question: 'Como saber o prazo e o orçamento?', answer: 'Conte o que precisa construir para conversar sobre escopo, prazo e orçamento. Esta página não apresenta preços ou prazos fixos.' },
            { id: 'integracoes', question: 'É possível integrar sistemas existentes?', answer: 'Integrações fazem parte dos serviços da Antares. Compartilhe quais sistemas usa para avaliarmos as possibilidades e os acessos necessários.' },
          ].map(item => <details key={item.id} data-analytics-faq={item.id}><summary>{item.question}</summary><p>{item.answer}</p></details>)}
        </section>
        <section
          className="antares-container contact-section"
          id="contato"
          aria-labelledby="contact-title"
        >
          <div className="contact-banner">
            <div>
              <h2 id="contact-title">
                O próximo passo começa
                <br />
                com uma <span>conversa.</span>
              </h2>
              <p>Conte o que sua empresa precisa.</p>
            </div>
            <ProjectContactForm />
          </div>
        </section>
      </main>
      <footer className="antares-footer">
        <div className="antares-container footer-main">
          <Logo />
          <nav aria-label="Navegação do rodapé">
            {navigation.map((item) => (
              <a key={item.href} href={item.href}>
                {item.label}
              </a>
            ))}
          </nav>
        </div>
        <div className="antares-container footer-legal">
          <p>
            © {new Date().getFullYear()} Antares Tecnologia GTA. CNPJ:
            18.987.440/0001-40.
            <br />
            <span data-clarity-mask="true">Razão social: THIAGO XAVIER PACHECO 32629393828</span>
          </p>
          <div>
            <a href="mailto:contato@antarestecnologiagta.com.br" data-analytics-id="footer_email" data-analytics-event="click_email" data-analytics-location="footer">
              contato@antarestecnologiagta.com.br
            </a>
            <span>
              <Link to="/privacidade">Privacidade</Link>
              <Link to="/termos">Termos de uso</Link>
              <CookiePreferencesButton />
              {process.env.NODE_ENV === "development" && process.env.REACT_APP_ANALYTICS_DEBUG === "true" && <Link to="/admin/metricas">Diagnóstico de métricas</Link>}
            </span>
          </div>
        </div>
      </footer>

    </div>
  );
}
